import { useEffect, useState } from "react";

import { Icon, Tooltip } from "metabase/ui";

import type { PermissionOption } from "../../types";

import {
  IconContainer,
  PermissionsSelectLabel,
  PermissionsSelectOptionRoot,
} from "./PermissionsSelectOption.styled";

interface PermissionsSelectOptionProps extends Omit<PermissionOption, "value"> {
  className?: string;
  hint?: string | null;
}

export function PermissionsSelectOption({
  label,
  icon,
  iconColor,
  className,
  hint,
}: PermissionsSelectOptionProps) {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

  // This tooltip is driven by a manually-controlled `opened` prop (so
  // hovering anywhere on the row, not just the icon, reveals it), which
  // disables Mantine's own Escape handling (it only wires up when `opened`
  // is left uncontrolled). Restore dismissibility by hand (WCAG 1.4.13
  // Content on Hover or Focus).
  useEffect(() => {
    if (!shouldShowTooltip) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShouldShowTooltip(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shouldShowTooltip]);

  return (
    <PermissionsSelectOptionRoot
      className={className}
      onMouseEnter={() => setShouldShowTooltip(true)}
      onMouseLeave={() => setShouldShowTooltip(false)}
    >
      <Tooltip label={hint} disabled={!hint} opened={shouldShowTooltip}>
        <IconContainer iconColor={iconColor}>
          <Icon name={icon} />
        </IconContainer>
      </Tooltip>
      <PermissionsSelectLabel>{label}</PermissionsSelectLabel>
    </PermissionsSelectOptionRoot>
  );
}
